import { useMemo } from 'react';
import { type GridColDef } from '@mui/x-data-grid';
import type { Chat } from '@trinity/chat';
import { Table, getDisplayCellValue } from './Table';
import { TableCell } from './Table.Cell';

type Props = {
  item: Chat.Attachment.TableInline;
};

type TableRow = Chat.AttachmentTableRow & {
  _gridId: string;
};

function toHeaderField(header: Chat.Attachment.TableHeader): string {
  if (typeof header === 'string') {
    return header;
  }

  return header.key;
}

function humanizeColumnName(name: string): string {
  return name
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toHeaderLabel(header: Chat.Attachment.TableHeader): string {
  if (typeof header === 'string') {
    return humanizeColumnName(header);
  }

  return header.label;
}

export const AttachmentTable = ({ item }: Props) => {
  const columns = useMemo<GridColDef[]>(() => {
    return item.data.headers.map((header) => {
      const field = toHeaderField(header);
      return {
        field,
        type: 'string',
        minWidth: 100,
        maxWidth: 400,
        headerName: toHeaderLabel(header),
        editable: false,
        hideable: true,
        valueGetter: (_value, row: TableRow) => {
          const cellData = row[field];
          return String(getDisplayCellValue(cellData));
        },
        renderCell: TableCell,
      };
    });
  }, [item.data.headers]);

  return (
    <Table
      tableId={item.id}
      columns={columns}
      rows={item.data.rows}
      title={item.data.title}
      pinnedLeft={item.data.pinned?.left ?? []}
      showExportCsv={true}
    />
  );
};

AttachmentTable.displayName = 'Chat.Attachments.AttachmentTable';
